import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { fetchVerification } from "@/lib/api";
import { ShieldCheck, AlertTriangle, XCircle, ScanLine, Clock, ArrowRight } from "lucide-react";

const DEFAULT_WORKER = "marcus-reed";

const banners = {
  cleared: {
    bg: "bg-emerald-500",
    text: "text-white",
    icon: ShieldCheck,
    eyebrow: "Site eligibility · Green",
    title: "Ready for Site",
    subtitle: "All credentials valid for this site.",
  },
  review: {
    bg: "bg-amber-500",
    text: "text-[#0B1221]",
    icon: AlertTriangle,
    eyebrow: "Site eligibility · Amber",
    title: "Action Required",
    subtitle: "One or more credentials expiring soon.",
  },
  not_cleared: {
    bg: "bg-red-600",
    text: "text-white",
    icon: XCircle,
    eyebrow: "Site eligibility · Red",
    title: "Not Eligible",
    subtitle: "Expired credential blocks site entry.",
  },
};

const fmtDate = (d) => {
  try {
    const [y, m, day] = d.split("-");
    return `${m}/${day}/${y}`;
  } catch {
    return d;
  }
};

export default function QRVerify() {
  const { workerId } = useParams();
  const id = workerId || DEFAULT_WORKER;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    setLoading(true);
    fetchVerification(id)
      .then(setData)
      .catch((e) => setErr(e?.message || "Failed"))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-20 text-center text-slate-500" data-testid="verify-loading">
        Scanning credentials…
      </div>
    );
  }

  if (err || !data) {
    return (
      <div className="mx-auto max-w-2xl px-6 py-20 text-center text-red-600" data-testid="verify-error">
        Could not verify worker.
      </div>
    );
  }

  const banner = banners[data.clearance.clearance] || banners.not_cleared;
  const BIcon = banner.icon;

  return (
    <div data-testid="qr-verify-page" className="min-h-[calc(100vh-65px)] bg-[#F8FAFC]">
      <div className="mx-auto max-w-2xl px-6 py-10 md:py-14">
        {/* Scan header */}
        <div className="mb-6 flex items-center justify-between">
          <div className="inline-flex items-center gap-2 border border-slate-200 bg-white px-2.5 py-1.5 font-mono text-[11px] uppercase tracking-[0.22em] text-slate-600">
            <ScanLine className="h-3.5 w-3.5 text-[#FF5A1F]" strokeWidth={2.5} />
            ProCred / Foreman View
          </div>
          <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500" data-testid="scanned-at">
            Scanned · {new Date(data.scanned_at).toLocaleTimeString()}
          </div>
        </div>

        {/* Worker header */}
        <div className="border border-slate-200 bg-white p-6" data-testid="verify-worker-card">
          <div className="flex items-center gap-5">
            <div className="h-16 w-16 shrink-0 overflow-hidden border border-slate-200 bg-slate-100">
              {data.worker.photo_url ? (
                <img src={data.worker.photo_url} alt={data.worker.name} className="h-full w-full object-cover" />
              ) : (
                <div className="flex h-full w-full items-center justify-center font-display text-xl font-black text-slate-400">
                  {data.worker.name.split(" ").map((n) => n[0]).join("")}
                </div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="font-display text-2xl font-bold tracking-tight text-[#0B1221]" data-testid="verify-worker-name">
                  {data.worker.name}
                </h1>
                {data.worker.verified && (
                  <span
                    data-testid="verify-procred-badge"
                    className="inline-flex items-center gap-1 border border-[#0B1221] bg-[#0B1221] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-white"
                  >
                    <ShieldCheck className="h-3 w-3 text-[#FF5A1F]" strokeWidth={2.5} />
                    ProCred Verified
                  </span>
                )}
                {data.worker.identity_verified && (
                  <span
                    data-testid="verify-identity-badge"
                    className="inline-flex items-center gap-1 border border-emerald-300 bg-emerald-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-emerald-700"
                  >
                    <ShieldCheck className="h-3 w-3" strokeWidth={2.5} />
                    Identity Verified
                  </span>
                )}
              </div>
              <div className="mt-0.5 text-sm font-medium text-slate-700" data-testid="verify-worker-trade">
                {data.worker.trade}
              </div>
              <div className="mt-1 text-xs text-slate-500">{data.worker.employer}</div>
            </div>
          </div>
        </div>

        {/* Massive decision banner */}
        <div className={`mt-5 ${banner.bg} ${banner.text} p-7`} data-testid="verify-banner">
          <div className={`font-mono text-[11px] uppercase tracking-[0.22em] ${banner.text === "text-white" ? "opacity-80" : "text-[#0B1221]/70"}`}>
            {banner.eyebrow}
          </div>
          <div className="mt-3 flex items-center gap-4">
            <BIcon className="h-12 w-12 shrink-0" strokeWidth={2} />
            <div>
              <div className="font-display text-4xl font-black leading-none tracking-tight" data-testid="verify-decision">
                {banner.title}
              </div>
              <div className="mt-1 text-sm opacity-90" data-testid="verify-decision-label">
                {data.clearance.label}
              </div>
            </div>
          </div>
        </div>

        {/* Tagline */}
        <p
          data-testid="verify-tagline"
          className="font-display mt-6 text-center text-lg font-semibold tracking-tight text-[#0B1221]"
        >
          One scan. One answer. No paperwork chase.
        </p>

        {/* Credential summary */}
        <div className="mt-8 grid grid-cols-4 border border-slate-200 bg-white" data-testid="verify-credential-summary">
          {[
            { k: "Total", v: data.credential_summary.total, color: "text-[#0B1221]" },
            { k: "Valid", v: data.credential_summary.valid, color: "text-emerald-600" },
            { k: "Expiring", v: data.credential_summary.expiring, color: "text-amber-600" },
            { k: "Expired", v: data.credential_summary.expired, color: "text-red-600" },
          ].map((s, i) => (
            <div key={s.k} className={`p-4 text-center ${i < 3 ? "border-r border-slate-200" : ""}`}>
              <div className={`font-display text-3xl font-black tracking-tight ${s.color}`} data-testid={`summary-${s.k.toLowerCase()}`}>
                {s.v}
              </div>
              <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                {s.k}
              </div>
            </div>
          ))}
        </div>

        {/* Issues */}
        {data.issues.length > 0 && (
          <div className="mt-6 border border-slate-200 bg-white">
            <div className="border-b border-slate-200 px-5 py-3">
              <div className="font-mono text-[11px] uppercase tracking-[0.22em] text-slate-500">
                Action items
              </div>
              <div className="font-display mt-0.5 text-base font-bold text-[#0B1221]">
                Credentials needing attention
              </div>
            </div>
            <ul className="divide-y divide-slate-100">
              {data.issues.map((issue, idx) => {
                const Icon = issue.status === "expired" ? XCircle : Clock;
                const color = issue.status === "expired" ? "text-red-600" : "text-amber-600";
                return (
                  <li key={idx} className="flex items-center justify-between gap-4 px-5 py-3" data-testid={`verify-issue-${idx}`}>
                    <div className="flex items-center gap-3">
                      <Icon className={`h-4 w-4 ${color}`} strokeWidth={2.25} />
                      <div>
                        <div className="text-sm font-semibold text-[#0B1221]">{issue.name}</div>
                        <div className="text-xs text-slate-500">Expires {fmtDate(issue.expires)}</div>
                      </div>
                    </div>
                    <span className={`font-mono text-[10px] uppercase tracking-[0.2em] ${color}`}>
                      {issue.status === "expired" ? "Expired" : "Expiring"}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

        {/* Foreman actions */}
        <div className="mt-6 flex flex-wrap gap-3">
          <button
            data-testid="verify-action-contact"
            onClick={() => alert("Foreman notified worker's employer.")}
            className="inline-flex items-center gap-2 bg-[#0B1221] px-5 py-3 text-sm font-semibold uppercase tracking-wider text-white transition-colors hover:bg-[#1E293B]"
          >
            Contact Employer
            <ArrowRight className="h-4 w-4" />
          </button>
          <Link
            to="/dashboard"
            data-testid="verify-action-dashboard"
            className="inline-flex items-center gap-2 border border-slate-300 bg-white px-5 py-3 text-sm font-semibold uppercase tracking-wider text-[#0B1221] transition-colors hover:border-[#0B1221]"
          >
            Open Compliance Dashboard
          </Link>
        </div>
      </div>
    </div>
  );
}
